import { Paper, Flex, Text, Stack, Badge, Divider, Loader, Center, Box } from "@mantine/core";
import { formatPrice } from "@/shared/utils/formatCurrency";

interface PurchaseHistoryTabProps {
  transactions: {
    id: string;
    amount: number;
    source: string;
    createdAt: string | Date;
  }[];
  loading?: boolean;
  creditPriceInr?: number; // price per credit in paise
  creditPriceUsd?: number; // price per credit in cents
  currency?: "INR" | "USD";
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatTransactionAmount(
  credits: number,
  creditPriceInr?: number,
  creditPriceUsd?: number,
  currency: "INR" | "USD" = "USD"
): string {
  const pricePerCreditSubunit = currency === "INR" ? creditPriceInr : creditPriceUsd;
  if (!pricePerCreditSubunit) return `${credits} credits`;
  return formatPrice((credits * pricePerCreditSubunit) / 100, currency);
}

export default function PurchaseHistoryTab({
  transactions,
  loading,
  creditPriceInr,
  creditPriceUsd,
  currency = "USD",
}: PurchaseHistoryTabProps) {
  if (loading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  if (transactions.length === 0) {
    return (
      <Paper p="xl" radius="lg" withBorder>
        <Text c="slate.5" ta="center">
          No purchases yet
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {transactions.map((transaction) => (
        <Paper key={transaction.id} px="lg" py="md" radius="lg" style={{ border: "1px solid #E5E7EB" }}>
          {/* Desktop */}
          <Box visibleFrom="sm">
            <Flex justify="space-between" align="flex-start" mb="sm">
              <Text fz={22} fw={400}>
                {formatTransactionAmount(transaction.amount, creditPriceInr, creditPriceUsd, currency)}
              </Text>
              <Badge size="md" radius="sm" variant="light" color="green">
                Successful
              </Badge>
            </Flex>

            <Flex align="center" gap="md">
              <Text fz="sm" c="slate.6">
                Credits Purchase : {transaction.amount}
              </Text>
              <Divider orientation="vertical" />
              <Text fz="sm" c="slate.6">
                {formatDate(transaction.createdAt)}
              </Text>
            </Flex>
          </Box>

          {/* Mobile */}
          <Box hiddenFrom="sm">
            <Flex justify="space-between" align="flex-start" mb="sm">
              <Text fz={20} fw={400}>
                {formatTransactionAmount(transaction.amount, creditPriceInr, creditPriceUsd, currency)}
              </Text>
              <Badge size="sm" radius="sm" variant="light" color="green">
                Successful
              </Badge>
            </Flex>

            <Flex align="center" gap="sm">
              <Text fz="xs" c="slate.6">
                Credits Purchase : {transaction.amount}
              </Text>
              <Divider orientation="vertical" />
              <Text fz="xs" c="slate.6">
                {formatDate(transaction.createdAt)}
              </Text>
            </Flex>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
}
